// Sign-in by emailed link (DEC-140, DEC-141, SDD §38.4).
//
// There is no password anywhere in this file, and no token handling: the
// session arrives as an `HttpOnly` cookie the browser stores itself.

import { http } from './client'
import type { AuthClaims } from './client'

export interface RedeemResult {
  tenant_id: string
  tenant_name: string
  email: string
  name: string | null
  profile: string
}

/**
 * Every reason a sign-in can fail.
 *
 * The first five come from redeeming an emailed link (§38.7); the rest from a
 * Google sign-in (§38.11). They share one screen component because they land on
 * the same route, but each keeps its own message: collapsing them would tell an
 * invited contractor that something generic went wrong, when the actual answer,
 * "use the link we emailed you", is actionable.
 */
export type LinkFailureReason =
  | 'expired'
  | 'already_used'
  | 'revoked'
  | 'member_disabled'
  | 'not_found'
  | 'not_invited'
  | 'email_unverified'
  | 'domain_not_allowed'
  | 'denied'
  | 'invalid_state'
  | 'exchange_failed'
  | 'workspace_disabled'
  | 'signup_rate_limited'

/**
 * Spend an invitation or a sign-in link.
 *
 * **Called after the page loads, never by the navigation that opened it.**
 * Corporate mail scanners and browser link prefetchers issue GET requests
 * against links in email; if opening the URL spent the token, the recipient
 * would be shown "already used" for a link they never clicked.
 */
export async function redeemLink(token: string): Promise<RedeemResult> {
  return http.post<RedeemResult>('/auth/link/redeem', { token })
}

/**
 * Ask for a sign-in link.
 *
 * Resolves identically whether or not the address is enabled, by design: the
 * server discloses nothing, so neither can this. The caller must not branch on
 * the result.
 */
export async function requestLoginLink(email: string): Promise<{ message: string }> {
  return http.post<{ message: string }>('/auth/login/request', { email })
}

/** The signed-in identity, or a 401 when there is no live session. */
export async function fetchMe(): Promise<AuthClaims> {
  return http.get<AuthClaims>('/auth/me')
}

/** End the session server-side. The cookie is cleared by the response. */
export async function logout(): Promise<void> {
  await http.post<void>('/auth/logout', {})
}

/**
 * Pull the `reason` out of a failed redemption.
 *
 * The API answers 410 with `{detail: {reason, message}}`, which `client.ts`
 * flattens to a JSON string when it is not already a string. Parsing it back
 * here keeps that shape in one place.
 */
export function linkFailureReason(err: unknown): LinkFailureReason {
  const message = err instanceof Error ? err.message : String(err)
  for (const reason of [
    'workspace_disabled',
    'member_disabled',
    'already_used',
    'expired',
    'revoked',
    'not_found',
  ] as LinkFailureReason[]) {
    if (message.includes(reason)) return reason
  }
  return 'not_found'
}


// ---------------------------------------------------------------------------
// Google sign-in (DEC-144)
// ---------------------------------------------------------------------------

export interface AuthMethods {
  link: boolean
  google: boolean
}

/**
 * Which sign-in methods this deployment offers.
 *
 * Read before rendering the Google button: without this the login page shows
 * one on a deployment with no Google app configured, and it fails when clicked.
 */
export async function fetchAuthMethods(): Promise<AuthMethods> {
  return http.get<AuthMethods>('/auth/methods')
}

/**
 * Begin a Google sign-in.
 *
 * Fetches the consent URL and returns it; the caller navigates. A plain fetch
 * is used rather than a direct <a> so a misconfigured server surfaces as an
 * inline error instead of a dead end on Google's side. Same shape as the GitHub
 * connect button (DEC-091).
 */
export async function initiateGoogleSignIn(returnTo: string | null): Promise<string> {
  const resp = await http.post<{ authorize_url: string }>('/auth/google/initiate', {
    return_to: returnTo,
  })
  return resp.authorize_url
}


// ---------------------------------------------------------------------------
// Self-serve sign-up (DEC-202), approved by a Super Admin (DEC-205)
// ---------------------------------------------------------------------------

export interface SignupPreview {
  email: string
  suggested_name: string
  /** DEC-205: set when this address already asked. Older servers omit it. */
  request_status?: 'pending' | 'declined' | null
  request_message?: string | null
}

/** DEC-205: the request is stored; `approved` only for a Super Admin's own first sign-up. */
export interface SignupSubmitted {
  status: 'pending' | 'approved'
  email: string
  workspace_name: string | null
  message: string
}

/** What a sign-up link is for. Does not spend it. */
export async function signupPreview(token: string): Promise<SignupPreview> {
  return http.post<SignupPreview>('/auth/signup/preview', { token })
}

/** Ask for the workspace. It waits for a Super Admin; no session starts (DEC-205). */
export async function signupComplete(token: string, workspaceName: string): Promise<SignupSubmitted> {
  return http.post<SignupSubmitted>('/auth/signup/complete', { token, workspace_name: workspaceName })
}

export type SignupFailureReason =
  | 'not_found'
  | 'expired'
  | 'already_used'
  | 'already_member'
  | 'invalid_name'
  | 'rate_limited'
  | 'declined'

export function signupFailureReason(err: unknown): SignupFailureReason {
  const message = err instanceof Error ? err.message : String(err)
  for (const reason of [
    'already_member',
    'already_used',
    'expired',
    'invalid_name',
    'rate_limited',
    'declined',
    'not_found',
  ] as SignupFailureReason[]) {
    if (message.includes(reason)) return reason
  }
  return 'not_found'
}

/** The server's own sentence from a sign-up error, when it sent one (e.g. the decline date). */
export function signupFailureMessage(err: unknown): string | null {
  const message = err instanceof Error ? err.message : String(err)
  try {
    const parsed = JSON.parse(message) as { message?: unknown }
    return typeof parsed.message === 'string' ? parsed.message : null
  } catch {
    return null
  }
}
